// @flow
import React, { useEffect, useState } from 'react';
import { AddAfterExistsButton, AddBeforeExistsButton } from '../../common';
// Composers
import { connect } from 'react-redux';
import { userLoggedInConnector } from '../../user';
// Selectors
import {
  addingItemLoadingSelector,
  changingItemLoadingSelector,
  itemSelectorByProductId,
} from '../selectors';
// Components
import { NotifyMeWhenStockRenewed } from '../../products';
// Analytics
import * as Analytics from '../../common/analytics';
// Types
import type { Connector } from 'react-redux';
import type { LineItem } from '../types';
// Actions
import { addItemToCart, changeQuantity, deleteItem } from '../actions';
// Styles
import './AddToCartButton.css';

type OwnProps = {
  showNotifyMeButton: boolean,
  productId: number,
  unitForQuantity: 'g' | 'un',
  incrementQuantity: number,
  defaultQuantity: number,
  inStock: boolean,
  discontinued: boolean,
  observation: string,
};

type Props = {
  ...OwnProps,
  addingLoading: boolean,
  changingLoading: boolean,
  item: LineItem,
  disableInsteadOfDeleteOnMinimumQuantity?: boolean,
  disable?: boolean,
  largeSize?: boolean,
  handleAddItem: () => void,
  handleIncreaseQuantity: () => void,
  handleDecreaseQuantity: () => void,
  handleRemoveItem: (lineItemId: number) => Promise<void>,
};
const AddToCartButton = ({
  showNotifyMeButton,
  inStock,
  discontinued,
  loggedIn,
  item,
  productId,
  unitForQuantity,
  largeSize,
  changingLoading,
  addingLoading,
  handleAddItem,
  handleIncreaseQuantity,
  handleDecreaseQuantity,
  handleRemoveItem,
  handleNotifyMeWhenStockRenewed,
  handleUnnotifyMeWhenStockRenewed,
  disableInsteadOfDeleteOnMinimumQuantity,
  disable,
  handleAddGiftCard,
  // USE ANALYTICS
  analyticsViewSource,
  fatLevel,
  packagingName,
  eneableFatLevel,
  eneablePackagingName,
  portionQuantity,
  setPortionQuantity,
  setSliderMaxQuantity,
}) => {
  const [addedItem, setAddedItem] = useState(false);
  const [decreasedQuantity, setDecreasedQuantity] = useState(false);
  const [itemCopy, setItemCopy] = useState(null);
  const [removingItem, setRemovingItem] = useState(false);
  // Read from the store (updated optimistically) so every button of the same item shows the same quantity
  const itemQuantity = item?.quantity;

  useEffect(() => {
    if (item && addedItem) {
      Analytics.logLineItemAdd(
        item,
        window.location.pathname,
        analyticsViewSource,
      );
      setAddedItem(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, addedItem]);

  useEffect(() => {
    if (!item && decreasedQuantity) {
      Analytics.logLineItemRemove(
        itemCopy,
        window.location.pathname,
        analyticsViewSource,
      );
      setDecreasedQuantity(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, decreasedQuantity]);

  const handleAddItemAction = event => {
    setAddedItem(true);
    handleAddItem();
  };

  const handleIncreaseQuantityAction = event => {
    const portion =
      portionQuantity === itemQuantity
        ? itemQuantity + item.variant.incrementQuantity
        : portionQuantity;
    setPortionQuantity && setPortionQuantity(portion);

    setSliderMaxQuantity &&
      setSliderMaxQuantity(itemQuantity + item.variant.incrementQuantity);
    handleIncreaseQuantity(portion, itemQuantity);
  };

  const handleDecreaseQuantityAction = event => {
    setDecreasedQuantity(true);
    let portion =
      portionQuantity > itemQuantity - item.variant.incrementQuantity
        ? itemQuantity - item.variant.incrementQuantity
        : portionQuantity;
    if (portion < item.variant.minimumQuantity) {
      portion = item.variant.minimumQuantity;
    }
    setItemCopy({ ...item });
    setPortionQuantity && setPortionQuantity(portion);
    if (itemQuantity > item.variant.minimumQuantity) {
      setSliderMaxQuantity &&
        setSliderMaxQuantity(itemQuantity - item.variant.incrementQuantity);
    }

    handleDecreaseQuantity(portion, itemQuantity);
  };

  const handleRemoveItemAction = async event => {
    setDecreasedQuantity(true);
    setItemCopy({ ...item });
    setRemovingItem(true);
    await handleRemoveItem(item.id);
    setRemovingItem(false);
  };

  // USE ANALYTICS

  // If a product is out of stock
  if (discontinued) {
    return (
      <div className="container">
        <h3 className="out-of-stock-text without-button">Descontinuado</h3>
      </div>
    );
  }
  if (!inStock) {
    return (
      <NotifyMeWhenStockRenewed
        loggedIn={loggedIn}
        showNotifyMeButton={showNotifyMeButton}
        productId={productId}
        largeSize={largeSize}
      />
    );
  }

  // Current product was not found in the lineItems. Show add to cart button
  if (!item || item?.giftCard?.id) {
    return (
      <AddBeforeExistsButton
        addItem={handleAddItemAction}
        loading={addingLoading}
        largeSize={largeSize}
        handleAddGiftCard={handleAddGiftCard}
        clickable={!disable}
      />
    );
  }

  const disableDecreaseQuantity =
    item.variant.minimumQuantity >= item.quantity &&
    disableInsteadOfDeleteOnMinimumQuantity;
  const disableIncrementQuantity =
    item.quantity + item.variant.incrementQuantity >
    item.variant.maximumQuantity;
  const addAfterExistsButton = (
    <AddAfterExistsButton
      unitForQuantity={unitForQuantity}
      currentQuantity={itemQuantity}
      increaseQuantity={handleIncreaseQuantityAction}
      decreaseQuantity={handleDecreaseQuantityAction}
      loading={changingLoading}
      disableDecreaseQuantity={
        disable || disableDecreaseQuantity || item.giftCard
      }
      disableIncrementQuantity={
        disable || disableIncrementQuantity || item.giftCard
      }
      largeSize={largeSize}
    />
  );

  // The cart line item already has its own delete button
  if (disableInsteadOfDeleteOnMinimumQuantity) {
    return addAfterExistsButton;
  }

  return (
    <div
      className={`add-to-cart-with-remove ${largeSize ? 'add-to-cart-with-remove-large' : ''}`}
    >
      {addAfterExistsButton}
      <button
        className="add-to-cart-remove-button"
        onClick={handleRemoveItemAction}
        disabled={disable || removingItem}
        title="Remover do carrinho"
        aria-label="Remover do carrinho"
      >
        <i
          className={removingItem ? 'fa fa-spinner fa-pulse' : 'fa fa-trash-o'}
          aria-hidden="true"
        />
      </button>
    </div>
  );
};

const mapStateToProps = (state, ownProps: OwnProps) => {
  const item = itemSelectorByProductId(state, ownProps.productId);
  const itemId = item ? item.id : null;
  return {
    item: item,
    addingLoading: addingItemLoadingSelector(state, ownProps.productId),
    changingLoading: changingItemLoadingSelector(state, itemId),
  };
};

const mapDispatchToProps = (dispatch: Dispatch, ownProps: OwnProps) => {
  return {
    handleAddItem: () => {
      dispatch(addItemToCart(ownProps.productId, ownProps.defaultQuantity));
    },
    handleIncreaseQuantity: (portion, itemQuantity) => {
      dispatch(
        changeQuantity(
          ownProps.productId,
          ownProps.incrementQuantity,
          ownProps.observation,
          portion,
          ownProps.eneableFatLevel && ownProps.fatLevel,
          ownProps.eneablePackagingName && ownProps.packagingName,
          itemQuantity,
        ),
      );
    },
    handleDecreaseQuantity: (portion, itemQuantity) => {
      dispatch(
        changeQuantity(
          ownProps.productId,
          -ownProps.incrementQuantity,
          ownProps.observation,
          portion,
          ownProps.eneableFatLevel && ownProps.fatLevel,
          ownProps.eneablePackagingName && ownProps.packagingName,
          itemQuantity,
        ),
      );
    },
    handleRemoveItem: (lineItemId: number) => dispatch(deleteItem(lineItemId)),
  };
};

AddToCartButton.defaultProps = {
  disableInsteadOfDeleteOnMinimumQuantity: false,
};

const connector: Connector<{}, Props> = connect(
  mapStateToProps,
  mapDispatchToProps,
);
export default userLoggedInConnector(connector(AddToCartButton));
